import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowForward,
  PlayArrow,
  Construction,
  Engineering,
  Architecture,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  fadeInUp,
  heroTextVariants,
  heroImageVariants,
  floatingAnimation,
  pulseAnimation,
} from '../styles/animations';
import illustation from '../illustration.png';
import cover1 from '../cover1.jpg';

const HeroSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const navigate = useNavigate();

  const stats = [
    { icon: <Construction />, number: '+15', label: t("contenu.texte6") },
    { icon: <Engineering />, number: '+30', label: t("contenu.texte7") },
    { icon: <Architecture />, number: '+11', label: t("contenu.texte8") },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: isMobile
          ? 'linear-gradient(135deg, #003d7a 0%, #1e5ba8 50%, #ff6b35 100%)'
          : `linear-gradient(135deg, rgba(0, 61, 122, 0.9) 0%, rgba(30, 91, 168, 0.8) 50%, rgba(255, 107, 53, 0.7) 100%), url(${cover1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: isMobile ? 'scroll' : 'fixed',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        component={motion.div}
        {...floatingAnimation}
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(0, 61, 122, 0.1)',
          filter: 'blur(40px)',
        }}
      />
      <Box
        component={motion.div}
        {...floatingAnimation}
        sx={{
          position: 'absolute',
          top: '60%',
          right: '10%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'rgba(255, 107, 53, 0.15)',
          filter: 'blur(60px)',
          animationDelay: '1s',
        }}
      />
      <Box
        component={motion.div}
        {...floatingAnimation}
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '15%',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'rgba(30, 91, 168, 0.1)',
          filter: 'blur(30px)',
          animationDelay: '2s',
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <motion.div variants={fadeInUp} initial="initial" animate="animate">
              <Box
                sx={{
                  pl: { xs: 0, md: 4 },
                  color: 'white',
                }}
              >
                {/* Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      padding: '8px 16px',
                      borderRadius: '50px',
                      mb: 3,
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <Construction sx={{ fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500}>
                      {t("contenu.texte26")}
                    </Typography>
                  </Box>
                </motion.div>

                {/* Main Title */}
                <motion.div variants={heroTextVariants} initial="initial" animate="animate">
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 700,
                      lineHeight: 1.2,
                      mb: 2,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    {t("contenu.texte1")}
                  </Typography>
                </motion.div>

                {/* Subtitle */}
                <motion.div variants={heroTextVariants} initial="initial" animate="animate">
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      fontWeight: 400,
                      lineHeight: 1.6,
                      mb: 4,
                      opacity: 0.9,
                    }}
                  >
                    {t("contenu.texte2")}
                  </Typography>
                </motion.div>

                {/* Description */}
                <motion.div variants={fadeInUp} initial="initial" animate="animate">
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      lineHeight: 1.7,
                      mb: 4,
                      opacity: 0.8,
                      maxWidth: '600px',
                    }}
                  >
                    {t("contenu.texte3")}
                  </Typography>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div variants={fadeInUp} initial="initial" animate="animate">
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/about')}
                      sx={{
                        backgroundColor: 'white',
                        // color: 'primary.main',
                        fontWeight: 600,
                        px: 4,
                        py: 2,
                        borderRadius: '50px',
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                        },
                      }}
                    >
                      {t("contenu.texte77")}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PlayArrow />}
                      onClick={() => navigate('/projects')}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 2,
                        borderRadius: '50px',
                        textTransform: 'none',
                        fontSize: '1rem',
                        backdropFilter: 'blur(10px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderColor: 'white',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      {t("contenu.texte10")}
                    </Button>
                  </Box>
                </motion.div>

                {/* Stats */}
                <motion.div variants={fadeInUp} initial="initial" animate="animate">
                  <Box sx={{ mt: 6, display: { xs: 'none', md: 'flex' }, gap: 4 }}>
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 + index * 0.1 }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                            <Box sx={{ color: '#35bcffff', mr: 1 }}>
                              {stat.icon}
                            </Box>
                            <Typography variant="h4" fontWeight={700} color="white">
                              {stat.number}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                            {stat.label}
                          </Typography>
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          </Grid>

          {/* Right Content - Image */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <motion.div
                variants={heroImageVariants}
                initial="initial"
                animate="animate"
                style={{ position: 'relative' }}
              >
                {/* Main Image */}
                <Avatar
                  src={illustation}
                  sx={{
                    width: { xs: 250, md: 400 },
                    height: { xs: 250, md: 400 },
                    border: '4px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  }}
                />

                {/* Floating Elements */}
                <motion.div
                  {...pulseAnimation}
                  style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff6b35, #ff9558)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 10px 30px rgba(255, 107, 53, 0.4)',
                  }}
                >
                  <Construction sx={{ fontSize: 40, color: 'white' }} />
                </motion.div>

                <motion.div
                  {...floatingAnimation}
                  style={{
                    position: 'absolute',
                    bottom: '-10px',
                    left: '-10px',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1a237e, #3949ab)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 25px rgba(26, 35, 126, 0.4)',
                  }}
                >
                  <Engineering sx={{ fontSize: 30, color: 'white' }} />
                </motion.div>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        style={{
          position: 'absolute',
          bottom: 30,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <Box
          component={motion.div}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          sx={{
            width: 30,
            height: 50,
            border: '2px solid rgba(255, 255, 255, 0.5)',
            borderRadius: '25px',
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 8,
              backgroundColor: 'white',
              borderRadius: '2px',
            }}
          />
        </Box>
      </motion.div>
    </Box>
  );
};

export default HeroSection;
